import { FunctionComponent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAtom } from 'jotai';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';

import { GridColDef } from '@mui/x-data-grid-pro';

import {
  DataGrid,
  Page,
  SearchBox,
  StyledButton,
  StyledIconButton,
  Typography
} from '../../components/interface';

import { AlertSomethingWentWrong } from '../../components/alerts/SomethingWentWrong';

import { GeneratedPain } from '../../models/GeneratedPain';

import { fetch_all } from '../../lib/server_helper';
import { loginAtom } from '../../lib/auth';

import { PAIN_ACCOUNTS } from './accounts';

const { DateTime } = require("luxon");

const formatEur = (v: number | string) => parseFloat(String(v || 0)).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');

type PainFilesProps = {};

export const PainFiles: FunctionComponent<PainFilesProps> = () => {
  const params = useParams();

  const [login] = useAtom(loginAtom);

  const accountType = params.account_type || '';
  const account = PAIN_ACCOUNTS.find((a) => a.name.toLowerCase() === accountType);

  const [files, setFiles] = useState<GeneratedPain[]>([]);
  const [filesCount, setFilesCount] = useState<number>(0);
  const [loader, setLoader] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [order, setOrder] = useState<string>('created_at DESC');
  const [search, setSearch] = useState<string>('');
  const [serverErrorSnackOpen, setServerErrorSnackOpen] = useState<boolean>(false);

  useEffect(() => {
    onSearch();
  }, [page, order]);

  const onSearch = (s?: string) => {
    setLoader(true);
    fetch_all<GeneratedPain>('/nl/v3/objects/fetch_all', {
      object: 'generated_pain',
      fields: ['id', 'created_at', 'account_type', 'note', 'is_sent', 'sent_at', 'unique_name', 'line_count', 'total_amount', 'get_item'],
      per_page: 20,
      page: page,
      order: order,
      filter: {
        search: !!s ? s : search,
        advanced: {
          account_type: accountType
        }
      }
    }, (obs, complete_data) => {
      setFiles(obs);

      let count = 0;
      if (typeof(complete_data?.data) === 'object' && 'count' in complete_data?.data) count = complete_data?.data?.count;
      setFilesCount(count);

      setLoader(false);
    }, (z) => {
      setServerErrorSnackOpen(true);
    }, login);
  };

  const columns: GridColDef[] = [
    { field: 'unique_name', headerName: 'File', width: 280 },
    { field: 'line_count', headerName: 'Rows', width: 100, align: 'right' },
    { field: 'total_amount', headerName: 'Total (EUR)', width: 150, align: 'right', valueFormatter: (params) => formatEur(params?.value || 0) },
    {
      field: 'is_sent',
      headerName: 'Sent',
      width: 200,
      renderCell: (params) => !!params.value
        ? <Typography sx={{color: 'tertiary.main'}}>{!!params.row.sent_at ? DateTime.fromISO(params.row.sent_at).toFormat('D TT') : 'Sent'}</Typography>
        : <Typography sx={{color: 'primary_grey.main'}}>Not sent</Typography>
    },
    { field: 'created_at', headerName: 'Created', width: 200, valueFormatter: (params) => DateTime.fromISO(params?.value).toFormat('D TT') },
    { field: 'note', headerName: 'Note', width: 250, sortable: false },
    { field: 'id', headerName: 'ID', width: 100, sortable: false }
  ];

  return (
    <Page title={`PAIN files${!!account ? ` - ${account.name}` : ''}`}
    startEndCrumb={<StyledButton
      label="Back to overview"
      id='back'
      text
      startIcon={<KeyboardBackspaceIcon />}
      path={`/create_pain`}
      sx={{marginBottom: 1}}
    />}>
      <SearchBox
        simple
        hide_reset
        memo="pain_files"
        onSearch={(s) => {
          setPage(0);
          setSearch(s);
          onSearch(s);
        }}
      />
      <DataGrid
        action_count={1}
        actions={(params) => {
          return (<>
            {!!params.row.get_item && <StyledIconButton color="primary" title="Download file" onClick={() => {
              window.open(params.row.get_item, "_BLANK");
            }}>
              <FileDownloadIcon />
            </StyledIconButton>}
          </>);
        }}
        objects={files}
        columns={columns}
        objectCount={filesCount}
        loader={loader}
        onSortModelChange={(a, b) => {
          let a_0_field = a[0].field;
          setOrder([a_0_field, a[0].sort].join(' '));
        }}
        page={page}
        setPage={(p) => {setPage(p);}}
      />

      {!!serverErrorSnackOpen && <AlertSomethingWentWrong open={serverErrorSnackOpen} setOpen={(b) => {setServerErrorSnackOpen(b);}} />}
    </Page>
  );
}
